'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { Users, ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

const OperatorBarChart = dynamic(
  () => import('@/app/analytics-dashboard/components/OperatorBarChart'),
  { ssr: false, loading: () => <div className="h-48 skeleton-pulse rounded" /> }
);

// Backend integration point: GET /api/analytics/operators?date=2026-06-08
export interface OperatorRow {
  id: string;
  name: string;
  device: string;
  shift: string;
  tasksAssigned: number;
  tasksCompleted: number;
  avgDurationMin: number;
  pickingAccuracy: number;
  kittingAccuracy: number;
  productivityScore: number;
  status: 'available' | 'kitting' | 'picked' | 'dispatched';
}

const operatorData: OperatorRow[] = [
  { id: 'op-001', name: 'Prasun Sharma', device: 'HH-01', shift: 'Morning', tasksAssigned: 32, tasksCompleted: 29, avgDurationMin: 10.2, pickingAccuracy: 99.1, kittingAccuracy: 98.7, productivityScore: 94, status: 'kitting' },
  { id: 'op-002', name: 'Kavita Menon', device: 'HH-02', shift: 'Morning', tasksAssigned: 28, tasksCompleted: 28, avgDurationMin: 9.8, pickingAccuracy: 100.0, kittingAccuracy: 99.3, productivityScore: 98, status: 'available' },
  { id: 'op-003', name: 'Rajesh Iyer', device: 'HH-03', shift: 'Morning', tasksAssigned: 24, tasksCompleted: 21, avgDurationMin: 13.6, pickingAccuracy: 95.2, kittingAccuracy: 93.8, productivityScore: 76, status: 'picked' },
  { id: 'op-004', name: 'Deepa Nair', device: 'HH-04', shift: 'Afternoon', tasksAssigned: 30, tasksCompleted: 30, avgDurationMin: 11.1, pickingAccuracy: 98.4, kittingAccuracy: 97.6, productivityScore: 91, status: 'kitting' },
  { id: 'op-005', name: 'Suresh Pillai', device: 'HH-05', shift: 'Morning', tasksAssigned: 18, tasksCompleted: 16, avgDurationMin: 15.4, pickingAccuracy: 92.7, kittingAccuracy: 91.2, productivityScore: 68, status: 'available' },
  { id: 'op-006', name: 'Ananya Krishnan', device: 'HH-06', shift: 'Afternoon', tasksAssigned: 26, tasksCompleted: 25, avgDurationMin: 10.7, pickingAccuracy: 97.8, kittingAccuracy: 98.1, productivityScore: 88, status: 'dispatched' },
  { id: 'op-007', name: 'Mohan Verma', device: 'HH-07', shift: 'Night', tasksAssigned: 22, tasksCompleted: 20, avgDurationMin: 12.3, pickingAccuracy: 96.5, kittingAccuracy: 95.4, productivityScore: 82, status: 'available' },
  { id: 'op-008', name: 'Priya Chandran', device: 'HH-08', shift: 'Night', tasksAssigned: 20, tasksCompleted: 19, avgDurationMin: 11.8, pickingAccuracy: 98.9, kittingAccuracy: 97.2, productivityScore: 87, status: 'picked' },
];

type SortKey = keyof OperatorRow;

function ScoreBar({ score }: { score: number }) {
  const color =
    score >= 90 ? 'bg-primary' : score >= 75 ? 'bg-accent' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-300`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-bold tabular-nums w-8 text-right ${score >= 90 ? 'text-primary' : score >= 75 ? 'text-accent' : 'text-red-400'}`}>
        {score}
      </span>
    </div>
  );
}

function AccuracyCell({ value }: { value: number }) {
  const color = value >= 98 ? 'text-primary' : value >= 95 ? 'text-foreground' : 'text-red-400';
  return <span className={`text-sm font-semibold tabular-nums ${color}`}>{value.toFixed(1)}%</span>;
}

export default function OperatorAnalyticsSection() {
  const [sortKey, setSortKey] = useState<SortKey>('productivityScore');
  const [sortAsc, setSortAsc] = useState(false);

  const sorted = [...operatorData].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortAsc ? av - bv : bv - av;
    }
    return sortAsc
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={11} className="text-muted-foreground/50" />;
    return sortAsc ? <ChevronUp size={11} className="text-primary" /> : <ChevronDown size={11} className="text-primary" />;
  };

  const cols: { key: SortKey; label: string; width: string }[] = [
    { key: 'name', label: 'Operator', width: 'w-36' },
    { key: 'device', label: 'Device', width: 'w-16' },
    { key: 'shift', label: 'Shift', width: 'w-20' },
    { key: 'tasksAssigned', label: 'Assigned', width: 'w-16' },
    { key: 'tasksCompleted', label: 'Completed', width: 'w-20' },
    { key: 'avgDurationMin', label: 'Avg Time', width: 'w-18' },
    { key: 'pickingAccuracy', label: 'Pick Acc.', width: 'w-20' },
    { key: 'kittingAccuracy', label: 'Kit Acc.', width: 'w-20' },
    { key: 'productivityScore', label: 'Score', width: 'w-32' },
  ];

  return (
    <div className="space-y-4">
      {/* Bar chart */}
      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-3">
          <Users size={15} className="text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Operator Productivity Scores</h3>
        </div>
        <OperatorBarChart data={operatorData} />
      </div>

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Users size={15} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Operator Performance — Today</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{operatorData.length} operators on shift</span>
          </div>
        </div>

        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-max">
            <thead>
              <tr className="border-b border-border">
                {cols.map((col) => (
                  <th
                    key={`th-${col.key}`}
                    className={`px-4 py-2.5 text-left ${col.width}`}
                  >
                    <button
                      onClick={() => handleSort(col.key)}
                      className="flex items-center gap-1 text-2xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                    >
                      {col.label}
                      <SortIcon col={col.key} />
                    </button>
                  </th>
                ))}
                <th className="px-4 py-2.5 text-left w-24">
                  <span className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Status</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sorted.map((op) => (
                <tr key={op.id} className="row-hover transition-colors duration-100">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{op.name}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono-data text-muted-foreground">{op.device}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{op.shift}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-foreground tabular-nums">{op.tasksAssigned}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-semibold tabular-nums ${op.tasksCompleted === op.tasksAssigned ? 'text-primary' : 'text-foreground'}`}>
                      {op.tasksCompleted}
                    </span>
                    <span className="text-xs text-muted-foreground">/{op.tasksAssigned}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-semibold tabular-nums ${op.avgDurationMin > 13 ? 'text-red-400' : 'text-foreground'}`}>
                      {op.avgDurationMin.toFixed(1)}m
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <AccuracyCell value={op.pickingAccuracy} />
                  </td>
                  <td className="px-4 py-3">
                    <AccuracyCell value={op.kittingAccuracy} />
                  </td>
                  <td className="px-4 py-3 w-32">
                    <ScoreBar score={op.productivityScore} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={op.status} size="sm" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              Avg Productivity:{' '}
              <span className="text-foreground font-semibold">
                {Math.round(operatorData.reduce((s, o) => s + o.productivityScore, 0) / operatorData.length)}
              </span>
            </span>
            <span>
              Avg Kitting Acc:{' '}
              <span className="text-foreground font-semibold">
                {(operatorData.reduce((s, o) => s + o.kittingAccuracy, 0) / operatorData.length).toFixed(1)}%
              </span>
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            Sorted by:{' '}
            <span className="text-foreground font-medium capitalize">{String(sortKey).replace(/([A-Z])/g, ' $1')}</span>
          </span>
        </div>
      </div>
    </div>
  );
}